/**
 * One-click (session-key) trading.
 *
 * When enabled, the user's MAIN wallet signs a single on-chain `ApproveAgent` message that
 * authorizes a freshly-generated browser-held "agent" key to sign orders on their behalf. That
 * approval is submitted gaslessly by the relayer. Afterwards every order is signed automatically
 * by the agent key with NO wallet popup, and relayed (user pays no gas) — the Hyperliquid /
 * Lighter / Pacifica model.
 *
 * Security: the agent key is generated client-side and kept in localStorage. It is TRADE-ONLY —
 * the PerpEngine never lets an agent move funds (deposit/withdraw stay signer==owner), so a
 * leaked session key can place orders but can never withdraw. The approval is time-boxed by
 * `expiry`; disabling discards the key locally and (optionally) revokes it on-chain.
 */
import { ethers } from "ethers";
import { Web3Provider } from "@ethersproject/providers";
import { fetchRelayerNonce, parseRelayerJson } from "./relayerResponse";
import { StoredAgent } from "./oneClick.types";
import { keyFor, loadAll, saveAll } from "./oneClick.storage";

export type { StoredAgent };
export {
  getOneClickState,
  isOneClickActive,
  getAgentWallet,
  getPendingRevokes,
} from "./oneClick.storage";
export {
  disableOneClick,
  revokePendingAgent,
  OneClickRevokeError,
} from "./oneClick.revoke";

const DEFAULT_DURATION_DAYS = 30;

/**
 * Enable one-click: generate an agent key, have the main wallet sign ApproveAgent once, relay it
 * gaslessly, and persist the key. Resolves when the approval tx is accepted by the relayer.
 */
const APPROVE_AGENT_TYPES = {
  ApproveAgent: [
    { name: "trader", type: "address" },
    { name: "agent", type: "address" },
    { name: "expiry", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

// Approve `agentAddress` on ONE engine (perp or spot — separate EIP-712 domains, so each needs its
// own main-wallet signature). The main wallet signs once per engine; the relayer submits gaslessly.
async function _approveAgentOnEngine(opts: {
  provider: Web3Provider;
  relayer: string;
  account: string;
  chainId: number;
  engineAddress: string;
  domainName: string; // "PerpEngine" | "CLOBEngine" | modular equivalents
  nonceRouter: string; // "agent" | "agentspot"
  engineParam: string; // "perp" | "spot"
  agentAddress: string;
  expiry: number;
  deadline: number;
}): Promise<void> {
  const nonce = await fetchRelayerNonce(
    opts.relayer,
    opts.account,
    opts.nonceRouter,
    "Could not reach the relayer to enable one-click trading."
  );

  const domain = { name: opts.domainName, version: "1", chainId: opts.chainId, verifyingContract: opts.engineAddress };
  const message = {
    trader: opts.account,
    agent: opts.agentAddress,
    expiry: String(opts.expiry),
    nonce,
    deadline: String(opts.deadline),
  };
  /* eslint no-underscore-dangle: 0 */
  const signature = await opts.provider.getSigner()._signTypedData(domain, APPROVE_AGENT_TYPES, message);

  const relayRes = await fetch(`${opts.relayer}/relay`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      action: "approveAgent",
      params: {
        engine: opts.engineParam,
        trader: opts.account,
        agent: opts.agentAddress,
        expiry: String(opts.expiry),
        nonce,
        deadline: String(opts.deadline),
      },
      signature,
    }),
  });
  const relayJson: any = await parseRelayerJson(
    relayRes,
    "Failed to enable one-click trading."
  );
  if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
    throw new Error(relayJson?.error || "Failed to enable one-click trading. Please try again.");
  }
}

export async function enableOneClick({
  provider,
  account,
  chainId,
  perpEngine,
  spotEngine,
  agentRegistry,
  perpDomainName,
  spotDomainName,
  relayerUrl,
  durationDays = DEFAULT_DURATION_DAYS,
}: {
  provider: Web3Provider;
  account: string;
  chainId: number;
  perpEngine?: string;
  spotEngine?: string;
  agentRegistry?: string;
  perpDomainName?: string;
  spotDomainName?: string;
  relayerUrl: string;
  durationDays?: number;
}): Promise<StoredAgent> {
  const relayer = relayerUrl.replace(/\/+$/, "");
  const agent = ethers.Wallet.createRandom();
  const expiry = Math.floor(Date.now() / 1000) + durationDays * 24 * 3600;
  const deadline = Math.floor(Date.now() / 1000) + 3600;

  const ZERO = "0x0000000000000000000000000000000000000000";
  if (agentRegistry && agentRegistry !== ZERO) {
    // SHARED registry: ONE main-wallet signature approves the agent for spot AND perp.
    await _approveAgentOnEngine({
      provider, relayer, account, chainId,
      engineAddress: agentRegistry, domainName: "MarginXAgentRegistry", nonceRouter: "agentregistry",
      engineParam: "registry", agentAddress: agent.address, expiry, deadline,
    });
  } else {
    // No shared registry on this chain — approve per engine (one signature each).
    // Guard each engine so a spot-only (or perp-only) chain still enables cleanly.
    if (perpEngine && perpEngine !== ZERO) {
      await _approveAgentOnEngine({
        provider, relayer, account, chainId,
        engineAddress: perpEngine, domainName: perpDomainName || "PerpEngine", nonceRouter: "agent", engineParam: "perp",
        agentAddress: agent.address, expiry, deadline,
      });
    }
    if (spotEngine && spotEngine !== ZERO && spotEngine !== perpEngine) {
      await _approveAgentOnEngine({
        provider, relayer, account, chainId,
        engineAddress: spotEngine, domainName: spotDomainName || "CLOBEngine", nonceRouter: "agentspot", engineParam: "spot",
        agentAddress: agent.address, expiry, deadline,
      });
    }
  }

  const stored: StoredAgent = {
    agentPrivateKey: agent.privateKey,
    agentAddress: agent.address,
    expiry,
    enabled: true,
  };
  const map = loadAll();
  map[keyFor(account, chainId)] = stored;
  saveAll(map);
  return stored;
}

/**
 * Ensure the stored agent is approved on a SPECIFIC engine, approving it on demand (one wallet
 * popup, relayed gaslessly) if it isn't. Returns true if the agent is approved and can sign for
 * this engine, false if there's no usable agent or the check failed. This heals one-click sessions
 * that were enabled before an engine supported agents (e.g. perp-only agents placing spot orders).
 */
export async function ensureAgentApprovedOnEngine(opts: {
  provider: Web3Provider;
  account: string;
  chainId: number;
  engineAddress: string;
  domainName: string; // "PerpEngine" | "CLOBEngine"
  nonceRouter: string; // "agent" | "agentspot"
  engineParam: string; // "perp" | "spot"
  relayerUrl: string;
}): Promise<boolean> {
  const stored = loadAll()[keyFor(opts.account, opts.chainId)];
  if (!stored || !stored.enabled || stored.expiry <= Math.floor(Date.now() / 1000)) return false;

  const engine = new ethers.Contract(
    opts.engineAddress,
    ["function isApprovedAgent(address trader, address agent) view returns (bool)"],
    opts.provider
  );
  try {
    if (await engine.isApprovedAgent(opts.account, stored.agentAddress)) return true;
  } catch {
    return false; // can't verify — caller falls back to the connected wallet
  }

  // Not approved on this engine yet — approve it now (one main-wallet signature, relayed gaslessly).
  await _approveAgentOnEngine({
    provider: opts.provider,
    relayer: opts.relayerUrl.replace(/\/+$/, ""),
    account: opts.account,
    chainId: opts.chainId,
    engineAddress: opts.engineAddress,
    domainName: opts.domainName,
    nonceRouter: opts.nonceRouter,
    engineParam: opts.engineParam,
    agentAddress: stored.agentAddress,
    expiry: stored.expiry,
    deadline: Math.floor(Date.now() / 1000) + 3600,
  });
  return true;
}

/**
 * Heal a PERP one-click session on a registry-enabled chain. On chains with a shared AgentRegistry
 * the PerpEngine delegates agent auth to the registry and IGNORES a local per-engine grant, so a
 * session enabled before the registry became authoritative is silently ignored and perp orders fail
 * "Unauthorized signer". This checks the authoritative approval (PerpEngine.isApprovedAgent, which
 * delegates to the registry) and, when it's missing, re-approves via the REGISTRY (one wallet popup,
 * relayed) — never via the engine, which the relayer now rejects on registry chains.
 *
 * Returns true when the agent is (now) approved for perp, false when there's no usable session or the
 * check/approval failed (caller falls back to the connected wallet).
 */
export async function ensurePerpAgentApproved(opts: {
  provider: Web3Provider;
  account: string;
  chainId: number;
  perpEngine: string;
  agentRegistry?: string;
  perpDomainName?: string;
  relayerUrl: string;
}): Promise<boolean> {
  const stored = loadAll()[keyFor(opts.account, opts.chainId)];
  if (!stored || !stored.enabled || stored.expiry <= Math.floor(Date.now() / 1000)) return false;

  const engine = new ethers.Contract(
    opts.perpEngine,
    ["function isApprovedAgent(address trader, address agent) view returns (bool)"],
    opts.provider
  );
  try {
    if (await engine.isApprovedAgent(opts.account, stored.agentAddress)) return true;
  } catch {
    return false; // can't verify — caller falls back to the connected wallet
  }

  // Not approved (stale local grant). Re-approve via the authoritative target: the shared registry
  // when the chain has one, else the engine directly.
  const ZERO = "0x0000000000000000000000000000000000000000";
  const useRegistry = !!opts.agentRegistry && opts.agentRegistry !== ZERO;
  await _approveAgentOnEngine({
    provider: opts.provider,
    relayer: opts.relayerUrl.replace(/\/+$/, ""),
    account: opts.account,
    chainId: opts.chainId,
    engineAddress: useRegistry ? (opts.agentRegistry as string) : opts.perpEngine,
    domainName: useRegistry ? "MarginXAgentRegistry" : opts.perpDomainName || "PerpEngine",
    nonceRouter: useRegistry ? "agentregistry" : "agent",
    engineParam: useRegistry ? "registry" : "perp",
    agentAddress: stored.agentAddress,
    expiry: stored.expiry,
    deadline: Math.floor(Date.now() / 1000) + 3600,
  });
  return true;
}
